// apps/web/src/components/chat/forms/AddFundForm.tsx
import { Button, TextField, Typography, Box, Alert, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useState } from 'react';
import { createResource } from '@diggens/api-client';

interface FundFormProps {
  data: {};
  tenantId: string;
  onSuccess: (fundId: string) => void;
  onClose: () => void;
}

export function FundForm({ tenantId, onSuccess, onClose }: FundFormProps) {
  const [name, setName] = useState('');
  const [reportingFrequency, setReportingFrequency] = useState<'monthly' | 'quarterly' | 'annual'>('quarterly');
  const [reportingDay, setReportingDay] = useState(15);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsSaving(true);
    setError(null);

    if (!name.trim()) {
      setError('Fund name is required');
      setIsSaving(false);
      return;
    }

    try {
      const insertedData = await createResource<{ id: string }>('funds', {
        tenant_id: tenantId,
        name: name.trim(),
        reporting_frequency: reportingFrequency,
        reporting_day_of_month: reportingDay,
        reporting_timezone: 'UTC',
      });

      onSuccess(insertedData.id);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create fund');
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Create New Fund
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add a new investment fund to track portfolio companies and metrics.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField fullWidth label="Fund Name" value={name} onChange={(e) => setName(e.target.value)} required disabled={isSaving} />

        <FormControl fullWidth>
          <InputLabel>Reporting Frequency</InputLabel>
          <Select value={reportingFrequency} label="Reporting Frequency" onChange={(e) => setReportingFrequency(e.target.value as any)} disabled={isSaving}>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="annual">Annual</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Reporting Day of Month"
          type="number"
          value={reportingDay}
          onChange={(e) => setReportingDay(Number(e.target.value))}
          inputProps={{ min: 1, max: 31 }}
          helperText="Day of the month when reports are due (1-31)"
          disabled={isSaving}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} fullWidth disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" fullWidth disabled={isSaving}>
          {isSaving ? 'Creating...' : 'Create Fund'}
        </Button>
      </Box>
    </Box>
  );
}
